import { agentExecutionReadinessImpactKindSchema, agentExecutionReadinessImpactSourceSchema } from "../../agent-execution-readiness-impact-taxonomy.js";
import { array, boolean, discriminatedUnion, enum as enumFactory, int, iso, literal, null as nullFactory, number, strictObject, string, union } from "../../lazy-zod.js";
import { agentProfileIdSchema } from "../protocol/profile.js";
import { daemonServiceLaneSchema } from "../daemon/service-contract.js";
import { openMeldDaemonProviderAccessEvidenceProviderSchema } from "../daemon/task-result.js";
import { CURRENT_DAEMON_EXECUTION_CONTRACT_EPOCH } from "../daemon/runtime-transport.js";
import { agentControllerUsageLimitsSchema } from "../runtime/agent-controller-interface.js";
import { prepareReadinessRuntimeIdSchema } from "./prepare-readiness.js";
import { daemonRouteObservationSummarySchema } from "../daemon/route-observation-summary.js";
import { agentProfileBindingAgentControllerCollaborationModeSchema, agentProfileBindingAgentControllerPermissionModeSchema, agentProfileBindingModelSelectionSchema, agentProfileBindingProjectionSchema } from "./agent-profile-binding.js";
//#region src/schemas/http/agent-execution-readiness.ts
const ISO_DATE_TIME_SCHEMA = iso.datetime({ offset: true });
const trimmedNonEmptyStringSchema = string().trim().min(1);
const nullableTrimmedStringSchema = union([trimmedNonEmptyStringSchema, nullFactory()]);
const nullableIsoDateTimeSchema = union([ISO_DATE_TIME_SCHEMA, nullFactory()]);
const agentExecutionReadinessRouteParamsSchema = strictObject({ agentProfileId: agentProfileIdSchema });
const agentExecutionReadinessTargetRuntimeSchema = discriminatedUnion("kind", [strictObject({ kind: literal("current_local_runtime") }), strictObject({
	kind: literal("openmeld_service_runtime_id"),
	openMeldServiceRuntimeId: prepareReadinessRuntimeIdSchema
})]);
const getAgentExecutionReadinessBatchBodySchema = strictObject({
	agentProfileIds: array(agentProfileIdSchema).min(1).max(100),
	targetRuntime: agentExecutionReadinessTargetRuntimeSchema.optional()
});
const agentExecutionReadinessPrimaryActionSchema = enumFactory([
	"none",
	"connect_service",
	"reconnect_binding",
	"run_prepare",
	"update_service",
	"check_status",
	"fix_provider_account"
]);
const agentExecutionReadinessPrimaryActionPlanSchema = discriminatedUnion("action", [
	strictObject({
		action: literal("none"),
		target: nullFactory()
	}),
	strictObject({
		action: literal("connect_service"),
		target: strictObject({ kind: literal("openmeld_service_connection") })
	}),
	strictObject({
		action: literal("reconnect_binding"),
		target: union([strictObject({ kind: literal("choose_local_agent") }), strictObject({
			agentControllerRef: trimmedNonEmptyStringSchema,
			kind: literal("local_agent_connection")
		})])
	}),
	strictObject({
		action: literal("run_prepare"),
		target: strictObject({ kind: literal("current_computer") })
	}),
	strictObject({
		action: literal("update_service"),
		target: strictObject({ kind: literal("openmeld_service") })
	}),
	strictObject({
		action: literal("check_status"),
		target: strictObject({ kind: literal("status") })
	}),
	strictObject({
		action: literal("fix_provider_account"),
		target: strictObject({
			kind: literal("provider_account"),
			provider: openMeldDaemonProviderAccessEvidenceProviderSchema
		})
	})
]);
const wakeRouteEvidenceSchema = daemonRouteObservationSummarySchema;
const wakeRouteStatusSchema = discriminatedUnion("status", [strictObject({
	connectionGeneration: int().positive(),
	daemonSessionId: trimmedNonEmptyStringSchema,
	deviceId: trimmedNonEmptyStringSchema,
	lane: daemonServiceLaneSchema,
	lastHeartbeatMs: int().nonnegative(),
	leaseEpoch: int().positive(),
	status: literal("ready")
}), strictObject({ status: enumFactory([
	"checking",
	"offline",
	"unavailable"
]) })]);
const agentWakeRouteRegistrationStatusSchema = enumFactory([
	"registered",
	"pending",
	"skipped",
	"rejected",
	"expired",
	"unavailable"
]);
const agentWakeRouteRegistrationSchema = strictObject({
	status: agentWakeRouteRegistrationStatusSchema,
	reasonCode: nullableTrimmedStringSchema,
	reportedAtMs: number().int().nonnegative().nullable(),
	daemonSessionId: nullableTrimmedStringSchema,
	deviceId: nullableTrimmedStringSchema,
	lane: union([daemonServiceLaneSchema, nullFactory()]),
	serviceVersion: nullableTrimmedStringSchema
});
const agentWakeAvailabilityStatusSchema = enumFactory([
	"available_for_wake",
	"checking_connection",
	"offline_for_wake",
	"unavailable_for_wake"
]);
const agentWakeAvailabilityCategorySchema = enumFactory([
	"available",
	"checking",
	"offline",
	"unavailable"
]);
const agentWakeAvailabilityPhaseSchema = enumFactory([
	"available",
	"checking_route",
	"checking_runtime",
	"checking_service",
	"checking_status",
	"connect_local_agent",
	"offline",
	"provider_blocked",
	"provider_sign_in_needed",
	"service_not_connected",
	"service_update_required",
	"unavailable"
]);
const agentWakeAvailabilitySchema = strictObject({
	action: agentExecutionReadinessPrimaryActionSchema,
	badgeLabel: trimmedNonEmptyStringSchema,
	category: agentWakeAvailabilityCategorySchema,
	isRefreshing: boolean(),
	label: trimmedNonEmptyStringSchema,
	phase: agentWakeAvailabilityPhaseSchema,
	reasonCode: nullableTrimmedStringSchema,
	status: agentWakeAvailabilityStatusSchema,
	summary: trimmedNonEmptyStringSchema,
	tooltip: trimmedNonEmptyStringSchema
});
const agentExecutionReadinessSchema = strictObject({
	agentProfileId: agentProfileIdSchema,
	displayState: enumFactory([
		"ready",
		"not_ready",
		"blocked",
		"unknown"
	]),
	facts: strictObject({
		profile: strictObject({
			active: boolean(),
			reasonCodes: array(trimmedNonEmptyStringSchema),
			source: enumFactory([
				"not_checked",
				"profile_identity",
				"unavailable"
			])
		}),
		binding: strictObject({
			reasonCodes: array(trimmedNonEmptyStringSchema),
			routable: boolean(),
			source: enumFactory([
				"binding_truth",
				"blocked",
				"missing",
				"not_checked",
				"unavailable"
			])
		}),
		claim: strictObject({
			executionBlocked: boolean(),
			grantsClaimSuccess: literal(false),
			reasonCodes: array(trimmedNonEmptyStringSchema),
			source: enumFactory(["claim_diagnostics", "not_checked"])
		}),
		providerAccount: strictObject({
			blocked: boolean(),
			expiresAtMs: number().int().nonnegative().nullable(),
			observedAtMs: number().int().nonnegative().nullable(),
			provider: union([openMeldDaemonProviderAccessEvidenceProviderSchema, nullFactory()]),
			reasonCodes: array(trimmedNonEmptyStringSchema),
			source: enumFactory(["not_checked", "wake_failure"])
		}),
		providerConversation: strictObject({
			agentControllerConversationId: nullableTrimmedStringSchema,
			errorCode: nullableTrimmedStringSchema,
			errorMessage: nullableTrimmedStringSchema,
			errorType: nullableTrimmedStringSchema,
			expiresAtMs: number().int().nonnegative().nullable(),
			method: union([literal("codex_app_server_thread_resume"), nullFactory()]),
			observedAtMs: number().int().nonnegative().nullable(),
			provider: union([literal("codex"), nullFactory()]),
			ready: boolean(),
			reasonCodes: array(trimmedNonEmptyStringSchema),
			required: boolean(),
			source: enumFactory([
				"not_required",
				"runtime_reporting",
				"unavailable"
			]),
			status: enumFactory([
				"failed",
				"missing",
				"not_required",
				"passed",
				"unavailable"
			])
		}),
		readinessImpact: strictObject({
			blocked: boolean(),
			expiresAtMs: number().int().nonnegative().nullable(),
			impactKind: union([agentExecutionReadinessImpactKindSchema, nullFactory()]),
			observedAtMs: number().int().nonnegative().nullable(),
			reasonCodes: array(trimmedNonEmptyStringSchema),
			source: union([literal("not_checked"), agentExecutionReadinessImpactSourceSchema])
		}),
		localService: strictObject({
			reachable: boolean(),
			refreshable: boolean(),
			reasonCodes: array(trimmedNonEmptyStringSchema),
			source: enumFactory([
				"runtime_transport",
				"not_checked",
				"unavailable"
			])
		}),
		service: strictObject({
			executionCompatible: boolean(),
			expectedEpoch: literal(CURRENT_DAEMON_EXECUTION_CONTRACT_EPOCH),
			observedEpoch: union([trimmedNonEmptyStringSchema, nullFactory()]),
			reasonCodes: array(trimmedNonEmptyStringSchema),
			source: enumFactory([
				"runtime_transport",
				"not_checked",
				"unavailable"
			])
		}),
		route: strictObject({
			deviceId: nullableTrimmedStringSchema,
			lane: union([daemonServiceLaneSchema, nullFactory()]),
			observedAt: nullableIsoDateTimeSchema,
			online: boolean(),
			reasonCodes: array(trimmedNonEmptyStringSchema),
			source: enumFactory([
				"daemon_route",
				"hosted_route",
				"missing",
				"not_checked"
			])
		}),
		runtime: strictObject({
			ready: boolean(),
			reasonCodes: array(trimmedNonEmptyStringSchema),
			source: enumFactory([
				"missing",
				"native_runtime",
				"not_checked",
				"prepare_readiness"
			])
		})
	}),
	primaryAction: agentExecutionReadinessPrimaryActionSchema,
	reasonCodes: array(trimmedNonEmptyStringSchema)
});
const getAgentExecutionReadinessResponseSchema = strictObject({
	usageLimits: agentControllerUsageLimitsSchema.optional(),
	agentExecutionReadiness: agentExecutionReadinessSchema,
	agentWakeAvailability: agentWakeAvailabilitySchema,
	agentWakeRouteRegistration: agentWakeRouteRegistrationSchema,
	agentProfileId: agentProfileIdSchema,
	agentControllerRef: nullableTrimmedStringSchema,
	generatedAt: ISO_DATE_TIME_SCHEMA,
	ownerUserId: nullableTrimmedStringSchema,
	primaryBinding: agentProfileBindingProjectionSchema.nullable().optional(),
	primaryActionPlan: agentExecutionReadinessPrimaryActionPlanSchema,
	profileName: nullableTrimmedStringSchema,
	publicProfileConfiguration: strictObject({
		agentControllerCollaborationMode: agentProfileBindingAgentControllerCollaborationModeSchema.nullable(),
		agentControllerPermissionMode: agentProfileBindingAgentControllerPermissionModeSchema.nullable(),
		agentControllerRef: nullableTrimmedStringSchema,
		modelSelection: agentProfileBindingModelSelectionSchema.nullable()
	}).optional(),
	routeDeviceId: nullableTrimmedStringSchema,
	routeLane: union([daemonServiceLaneSchema, nullFactory()]),
	runtimeDeviceDisplayName: nullableTrimmedStringSchema,
	wakeRouteEvidence: wakeRouteEvidenceSchema,
	wakeRouteStatus: wakeRouteStatusSchema
});
const getAgentExecutionReadinessBatchResponseSchema = strictObject({ items: array(getAgentExecutionReadinessResponseSchema) });
//#endregion
export { agentExecutionReadinessPrimaryActionPlanSchema, agentExecutionReadinessPrimaryActionSchema, agentExecutionReadinessRouteParamsSchema, agentExecutionReadinessSchema, agentExecutionReadinessTargetRuntimeSchema, agentWakeAvailabilityCategorySchema, agentWakeAvailabilityPhaseSchema, agentWakeAvailabilitySchema, agentWakeAvailabilityStatusSchema, agentWakeRouteRegistrationSchema, agentWakeRouteRegistrationStatusSchema, getAgentExecutionReadinessBatchBodySchema, getAgentExecutionReadinessBatchResponseSchema, getAgentExecutionReadinessResponseSchema, wakeRouteEvidenceSchema, wakeRouteStatusSchema };

//# sourceMappingURL=agent-execution-readiness.js.map